// Conversations — Phase 1 (direct messages end-to-end) + directory people-picker &
// name resolution. Two-pane on desktop (inbox | thread); on mobile it's inbox OR
// thread. Full three-column + spaces/reactions/Dana come in later phases (docs 04).
import React, { useState } from 'react';
import { Video } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/app/hooks';
import { conversationsApi } from '../api/conversationsApi';
import { useConversations } from '../hooks/useConversations';
import { useDirectory } from '../hooks/useDirectory';
import { useTyping } from '../hooks/useTyping';
import { useAvailability } from '../hooks/useAvailability';
import { useMessagesRoute } from '../route';
import { InboxList, conversationLabel } from '../components/InboxList';
import { Thread } from '../components/Thread';
import { NewConversationMenu } from '../components/NewConversationMenu';
import { ContextPanel } from '../components/ContextPanel';
import type { ReplyTarget } from '../components/Composer';
import { PageBoundary } from '@/features/shared/components/PageBoundary';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { useIsMobile } from '@/features/shared/hooks/use-mobile';
import { Sheet, SheetContent, SheetTitle } from '@/features/shared/components/ui/sheet';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import toast from 'react-hot-toast';
import type { ConversationSummary } from '../types';
import { directCounterpart } from '../directCounterpart';
import type { RowAction } from '../rowActions';

const SPACE_ROLES = ['tenant_owner', 'owner', 'admin', 'tenant_admin', 'company_admin', 'hr_manager'];
// May assign work to others (mirrors work-management's work:assign — includes line managers).
const ASSIGN_ROLES = [...SPACE_ROLES, 'manager'];

const StreamDot: React.FC<{ status: 'connecting' | 'open' | 'closed' }> = ({ status }) => (
  <span
    title={`Live: ${status}`}
    className={`inline-block h-2 w-2 rounded-full ${
      status === 'open' ? 'bg-green-500' : status === 'connecting' ? 'bg-amber-400' : 'bg-muted'
    }`}
  />
);

export const ConversationsPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const { t } = useTranslation('conversations');
  const navigate = useNavigate();
  const currentUserId = useAppSelector((s) => s.auth.user?.id);
  const roles = useAppSelector((s) => s.auth.user?.roles);
  const activeId = conversationId ?? null;
  const directory = useDirectory();
  const messagesPath = useMessagesRoute();
  const { notifyTyping, label: typingLabel } = useTyping(activeId, directory.nameOf);

  const { inbox, inboxLoading, messages, messagesLoading, receipts, streamStatus, sendMessage, sendGif, sendPoll, sendFile, createAssignment, votePoll, toggleReaction, editMessage, deleteMessage, toggleFavorite, applyRowAction, reload, loadOlder, canLoadOlder, loadingOlder } =
    useConversations(activeId, currentUserId);
  const confirm = useConfirm();

  const [showPicker, setShowPicker] = useState(false);
  const [showContext, setShowContext] = useState(true);
  const [replyTo, setReplyTo] = useState<ReplyTarget | null>(null);
  // Details capabilities are entitlement-driven (tenant products), never hardcoded.
  const ent = useEntitlements();
  const isMobile = useIsMobile();
  const capabilities = {
    work: ent.hasProduct('work'),
    recognition: ent.hasProduct('performance'),
    meetings: ent.hasProduct('conversations'),
    music: ent.hasProductStrict('music'),
    files: true,
    dana: true,
  };
  const [meetingSignal, setMeetingSignal] = useState(0); // header video → open the Meetings composer
  const [filter, setFilter] = useState<'all' | 'unread' | 'favorites' | 'circles'>('all');
  const filteredInbox = inbox
    .filter((c) => {
      if (filter === 'unread') return c.unread_hint > 0;
      if (filter === 'favorites') return !!c.is_favorite;
      if (filter === 'circles') return c.type === 'GROUP';
      return true;
    })
    // Pin favorites to the top; the sort is stable so recency order is preserved within
    // the pinned and unpinned groups. (No-op in the Favorites view — all are favorites.)
    .sort((a, b) => (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0));
  const unreadCount = inbox.filter((c) => c.unread_hint > 0).length;
  const canManage = !!roles?.some((r) => SPACE_ROLES.includes(r.toLowerCase()));
  const canAssign = !!roles?.some((r) => ASSIGN_ROLES.includes(r.toLowerCase()));
  // "Get this off my list." Remove is instant and reversible (new activity unhides
  // it), so it needs no dialog; Leave and Archive change what other people see, so
  // they confirm first — through the app's own dialog (native dialogs are banned).
  const handleRowAction = async (c: ConversationSummary, action: RowAction) => {
    const name = conversationLabel(c, currentUserId, directory.nameOf);
    if (action === 'leave') {
      const ok = await confirm({
        title: t('row_actions.leave_title', { defaultValue: 'Leave this conversation' }),
        message: t('row_actions.leave_confirm', {
          defaultValue: 'You will be removed from “{{name}}”. Everyone else keeps the conversation.',
          name,
        }),
        confirmLabel: t('row_actions.leave', { defaultValue: 'Leave' }),
        danger: true,
      });
      if (!ok) return;
    } else if (action === 'archive') {
      const ok = await confirm({
        title: t('row_actions.archive_title', { defaultValue: 'Archive this conversation' }),
        message: t('row_actions.archive_confirm', {
          defaultValue: '“{{name}}” will be archived for everyone. Messages are kept, but nobody can post.',
          name,
        }),
        confirmLabel: t('row_actions.archive', { defaultValue: 'Archive' }),
        danger: true,
      });
      if (!ok) return;
    }
    try {
      await applyRowAction(c.id, action);
      toast.success(
        action === 'remove'
          ? t('row_actions.removed', { defaultValue: 'Removed from your list' })
          : action === 'leave'
            ? t('row_actions.left', { defaultValue: 'You left the conversation' })
            : t('row_actions.archived', { defaultValue: 'Conversation archived' }),
      );
      if (activeId === c.id) navigate(messagesPath());
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('row_actions.failed', { defaultValue: 'That didn’t work. Please try again.' })));
    }
  };

  const onCreated = (id: string) => {
    setShowPicker(false);
    reload();
    navigate(messagesPath(id));
  };

  const activeSummary = inbox.find((c) => c.id === activeId);
  // The people behind the DIRECT rows — one batched availability read for the
  // whole inbox (a courtesy dot on their avatar, the status inline in the header).
  const directPartnerIds = React.useMemo(
    () =>
      inbox
        .filter((c) => c.type === 'DIRECT')
        .map((c) => directCounterpart(c.participant_user_ids, currentUserId))
        .filter((u): u is string => !!u),
    [inbox, currentUserId],
  );
  const availabilityByUser = useAvailability(directPartnerIds);
  const activePartnerId =
    activeSummary?.type === 'DIRECT'
      ? directCounterpart(activeSummary.participant_user_ids, currentUserId)
      : undefined;
  const activeTitle = (() => {
    if (!activeSummary) return 'Conversation';
    if (activeSummary.title) return activeSummary.title;
    if (activeSummary.type === 'DIRECT') {
      const other = directCounterpart(activeSummary.participant_user_ids, currentUserId);
      return directory.nameOf(other) || 'Direct message';
    }
    if (activeSummary.type === 'GROUP') return 'Circle';
    return activeSummary.type.charAt(0) + activeSummary.type.slice(1).toLowerCase();
  })();

  return (
    <div className="flex h-[calc(100dvh-6rem-var(--kp-player-h,0px))] overflow-hidden rounded-xl border border-border bg-card md:h-[calc(100dvh-8rem-var(--kp-player-h,0px))]">
      {/* Inbox column — hidden on mobile once a thread is open. */}
      <aside
        className={`w-full flex-none flex-col border-r border-border md:flex md:w-80 ${
          activeId ? 'hidden md:flex' : 'flex'
        }`}
      >
        <div className="relative flex-none border-b border-border px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-base font-semibold text-charcoal">{t('title')}</h1>
            <div className="flex items-center gap-2">
              <StreamDot status={streamStatus} />
              <button
                type="button"
                onClick={() => setShowPicker((v) => !v)}
                className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-600"
              >
                {t('new')}
              </button>
            </div>
          </div>
          {showPicker && (
            <NewConversationMenu
              directory={directory}
              selfId={currentUserId}
              roles={roles}
              onCreated={onCreated}
              onClose={() => setShowPicker(false)}
            />
          )}
        </div>
        {/* Filter bar — All / Unread / Favorites / Circles. */}
        <div className="flex flex-none items-center gap-1 overflow-x-auto border-b border-border px-2 py-2">
          {([
            ['all', t('filters.all')],
            ['unread', unreadCount > 0 ? `${t('filters.unread')} · ${unreadCount}` : t('filters.unread')],
            ['favorites', t('filters.favorites')],
            ['circles', t('filters.circles')],
          ] as const).map(([key, lbl]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`flex-none rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                filter === key ? 'bg-primary-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}
            >
              {lbl}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          <InboxList
            inbox={filteredInbox}
            loading={inboxLoading}
            activeId={activeId}
            selfId={currentUserId}
            nameOf={directory.nameOf}
            avatarOf={(id) => directory.personOf(id)?.avatarUrl}
            availabilityOf={(id) => (id ? availabilityByUser[id] : undefined)}
            onSelect={(id) => navigate(messagesPath(id))}
            onToggleFavorite={toggleFavorite}
            onRowAction={handleRowAction}
            emptyLabel={filter === 'all' ? undefined : t(`filters.empty_${filter}`)}
          />
        </div>
      </aside>

      {/* Thread column. */}
      <section className={`min-w-0 flex-1 ${activeId ? 'flex' : 'hidden md:flex'} flex-col`}>
        {activeId ? (
          <>
            <button
              type="button"
              onClick={() => navigate(messagesPath())}
              className="flex-none px-4 pt-2 text-left text-xs text-primary-600 md:hidden"
            >
              ← {t('back')}
            </button>
            <div className="min-h-0 flex-1">
              <PageBoundary compact title={t('thread_error', { defaultValue: 'This conversation could not be displayed' })}>
                <Thread
                  onLoadOlder={loadOlder}
                  canLoadOlder={canLoadOlder}
                  loadingOlder={loadingOlder}
                  receipts={receipts}
                  conversationId={activeId ?? undefined}
                  title={activeTitle}
                  messages={messages}
                  loading={messagesLoading}
                  currentUserId={currentUserId}
                  directory={directory}
                  showSenders={!!activeSummary && activeSummary.type !== 'DIRECT'}
                  headerAvatar={(() => {
                    if (activeSummary?.type !== 'DIRECT') return undefined;
                    const other = directCounterpart(activeSummary.participant_user_ids, currentUserId);
                    return { name: directory.nameOf(other) || activeTitle, url: directory.personOf(other)?.avatarUrl };
                  })()}
                  headerAvailability={activePartnerId ? availabilityByUser[activePartnerId] : undefined}
                  draftKey={activeId ?? undefined}
                  onSend={sendMessage}
                  onSendGif={sendGif}
                  onSendPoll={sendPoll}
                  onSendFile={sendFile}
                  onCreateAssignment={createAssignment}
                  onFileToCategory={(messageId, category) =>
                    conversationsApi.fileToCategory(activeId!, messageId, category)
                  }
                  canFileCompany={canManage}
                  canAssign={canAssign}
                  selfId={currentUserId}
                  onVotePoll={votePoll}
                  onReact={toggleReaction}
                  onEdit={editMessage}
                  onDelete={deleteMessage}
                  canModerate={canManage}
                  onReply={setReplyTo}
                  replyTo={replyTo}
                  onCancelReply={() => setReplyTo(null)}
                  onCancelMeeting={activeId ? (meetingId) => conversationsApi.cancelMeeting(activeId, meetingId).then(() => { reload(); }) : undefined}
                  onRescheduleMeeting={activeId ? (meetingId, input) => conversationsApi.rescheduleMeeting(activeId, meetingId, input).then(() => { reload(); }) : undefined}
                  onTyping={notifyTyping}
                  typingLabel={typingLabel}
                  headerRight={
                    <div className="flex flex-none items-center gap-1">
                      {!!activeSummary && (
                        <button
                          type="button"
                          onClick={() => { setShowContext(true); setMeetingSignal((n) => n + 1); }}
                          title={t('meeting.schedule', { defaultValue: 'Schedule Meeting' })}
                          aria-label={t('meeting.schedule', { defaultValue: 'Schedule Meeting' })}
                          className="hidden rounded-lg px-2 py-1 text-muted-foreground hover:bg-muted lg:block"
                        >
                          <Video className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowContext((v) => !v)}
                        title={showContext ? 'Hide details' : 'Show details'}
                        className={`hidden flex-none rounded-lg px-2 py-1 text-xs lg:block ${
                          showContext ? 'bg-primary-50 text-primary-700' : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {t('details')}
                      </button>
                    </div>
                  }
                />
              </PageBoundary>
            </div>
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
            <p>{t('select_prompt')}</p>
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600 md:hidden"
            >
              {t('new_message')}
            </button>
          </div>
        )}
      </section>

      {/* Context column (third) — desktop only, collapsible. Mobile/tablet reach it
          via the Details toggle later; hidden here to keep those layouts simple. */}
      {activeId && showContext && activeSummary && !isMobile && (
        <aside className="hidden w-80 flex-none overflow-y-auto border-l border-border lg:block">
          <PageBoundary compact>
            <ContextPanel
              conversation={activeSummary}
              directory={directory}
              selfId={currentUserId}
              canManage={canManage}
              onResync={() => conversationsApi.resyncSpace(activeSummary.id).catch(() => undefined)}
              onChanged={reload}
              onClose={() => setShowContext(false)}
              meetingSignal={meetingSignal}
              canConfigureIntegrations={canManage}
              onMeetingScheduled={reload}
              capabilities={capabilities}
              onLeftOrArchived={() => { setShowContext(false); reload(); }}
            />
          </PageBoundary>
        </aside>
      )}
      {/* Small screens: the same Details panel in a sheet (was desktop-only). */}
      {activeId && activeSummary && isMobile && (
        <Sheet open={showContext} onOpenChange={(o) => setShowContext(o)}>
          <SheetContent side="right" className="w-full p-0 sm:max-w-md">
            <SheetTitle className="sr-only">{t('details', { defaultValue: 'Details' })}</SheetTitle>
            <PageBoundary compact>
              <ContextPanel
                conversation={activeSummary}
                directory={directory}
                selfId={currentUserId}
                canManage={canManage}
                onResync={() => conversationsApi.resyncSpace(activeSummary.id).catch(() => undefined)}
                onChanged={reload}
                onClose={() => setShowContext(false)}
                meetingSignal={meetingSignal}
                canConfigureIntegrations={canManage}
                onMeetingScheduled={reload}
                capabilities={capabilities}
                onLeftOrArchived={() => { setShowContext(false); reload(); }}
              />
            </PageBoundary>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default ConversationsPage;
